// ============================================================
// TILT — SABOTAGE BUS
// ------------------------------------------------------------
// HARVESTED FROM: Terror's ResponseBridge.js — the registry +
// race-safe dispatch pattern, which solved exactly this problem
// (two React trees that must talk without importing each other).
//
// ── THE PROBLEM ──────────────────────────────────────────────
// In a duel, TWO TiltEngines are running. Yours and theirs (or
// yours and the NPC's). A sabotage cast on YOUR screen must land
// on THEIR table. But:
//
//   • In LOCAL/NPC duel, both engines live in one browser.
//   • In PvP, the other engine lives on another continent.
//
// The game screen must not care which. It calls `send(...)` and
// the sabotage arrives. That's it. The transport is swappable.
//
// ── WHY THIS ARCHITECTURE IS THE WHOLE PvP DESIGN ────────────
// The critical insight, and the reason SPLIT-SCREEN SABOTAGE is
// the flagship PvP mode instead of shared-table:
//
//   EACH PLAYER SIMULATES THEIR OWN TABLE, LOCALLY, AT FULL FPS.
//   ONLY DISCRETE SABOTAGE EVENTS CROSS THE WIRE.
//
// A shared-physics duel would need authoritative simulation +
// rollback netcode over Firebase RTDB. That is a months-long
// project and it would still feel bad on a 120ms connection.
//
// Split-screen sabotage needs to transmit: a power id, a point,
// a timestamp. Twelve bytes. It is IMMUNE to latency — a 200ms
// delay on an incoming Anchor is indistinguishable from your
// opponent having thought about it for 200ms longer.
//
// THE MODE THAT'S MOST FUN IS ALSO THE ONE THAT'S CHEAPEST TO
// BUILD. When that happens, you have found the right design.
//
// ── SAFETY (inherited from ResponseBridge) ───────────────────
//   • No transport registered → send() is a silent no-op. The
//     game NEVER stalls waiting on a bus that isn't there.
//   • A transport that throws is caught and swallowed. A dead
//     network must not be able to kill the physics loop.
//   • Every payload is validated against the POWER REGISTRY
//     before it's applied. A malformed/hostile packet from the
//     network cannot inject arbitrary behavior — it can only
//     name a power that already exists and is already legal.
//     THIS IS THE SECURITY BOUNDARY. Do not widen it.
// ============================================================

import { getPower } from "./Powers";
import { BOSS_MOVES, GUARDIAN_MOVES } from "./BossKit";

let transport = null;      // { send(payload), close() }
let inbox = null;          // (payload) => void — the local receiver
let resultInbox = null;    // (result)  => void — the DUEL-RESULT receiver (P2.8)

// ── The wire format. Small on purpose. ────────────────────────
//   { p: "anchor", x: 210, y: 340, from: "uid", t: 1720800000000, seq: 7 }
//
// `seq` is monotonic per-sender. If two sabotages arrive out of
// order (RTDB does not promise ordering across writes), the
// receiver applies them in seq order. This is the cheapest
// possible ordering guarantee and it's sufficient — sabotages
// commute almost perfectly (an Anchor and a Reverse don't care
// who went first), so we only need to prevent DUPLICATES, not
// enforce strict causality.
let _seq = 0;

export const makePayload = (powerId, point, from) => ({
  p: powerId,
  x: point ? Math.round(point.x) : null,
  y: point ? Math.round(point.y) : null,
  from: from || "?",
  t: Date.now(),
  seq: ++_seq,
});

/** Validate an incoming payload against the registry. THE GATE. */
export const validate = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const pw = getPower(payload.p);
  if (!pw) {
    // ── MONSTER VERBS — the second registry the wire honors ──
    // A boss move (the opponent attacking you AS their monster)
    // or a guardian verb (the opponent tending their monster,
    // which lives on YOUR table) rides the same frozen payload
    // shape: `p` names it, x/y are null, seq dedupes it. The
    // security boundary is UNCHANGED: a packet can only name an
    // id that exists in a registry we ship. Nothing else lands.
    const mv = BOSS_MOVES[payload.p] || GUARDIAN_MOVES[payload.p];
    if (!mv) return null;                      // unknown id → drop
    return {
      powerId: payload.p, point: null,
      from: String(payload.from || "?"), seq: payload.seq | 0,
    };
  }
  if (!pw.sabotage || !pw.onEnemy) return null; // not castable at enemy → drop
  const point =
    typeof payload.x === "number" && typeof payload.y === "number"
      ? { x: payload.x, y: payload.y }
      : null;
  if (pw.target === "point" && !point) return null;  // needs a point → drop
  return { powerId: pw.id, point, from: String(payload.from || "?"), seq: payload.seq | 0 };
};

// ── Transport registration ────────────────────────────────────
/**
 * Register the OUTBOUND channel. Called by whichever duel driver
 * is active — LocalDuel (NPC in the same tab) or FirebaseDuel.
 */
export const registerTransport = (t) => {
  transport = t && typeof t.send === "function" ? t : null;
};
export const unregisterTransport = () => {
  if (transport?.close) { try { transport.close(); } catch {} }
  transport = null;
};
export const hasTransport = () => !!transport;

/**
 * Register the INBOUND receiver — normally
 * `(payload) => myEngine.receiveSabotage(...)`.
 */
export const registerInbox = (fn) => {
  inbox = typeof fn === "function" ? fn : null;
};
export const unregisterInbox = () => { inbox = null; };

/**
 * Register the DUEL-RESULT receiver (P2.8 / D19). A felled avatar
 * lives in the OPPONENT's engine, so in real PvP the loser's own
 * `boss_felled` never fires — their local copy of their avatar is
 * fine. The winner tells them, over this channel.
 */
export const registerResultInbox = (fn) => {
  resultInbox = typeof fn === "function" ? fn : null;
};
export const unregisterResultInbox = () => { resultInbox = null; };

// ── THE RESULT PACKET (P2.8 / D19) ────────────────────────────
// A NEW MESSAGE TYPE, not a new field — the sabotage payload
// {p,x,y,from,t,seq} is FROZEN (roadmap P2.1), and widening a
// frozen shape without a rules deploy is the silent-failure
// afternoon the Firebase law exists to prevent. So results ride
// their OWN frozen shape, on their OWN rules node:
//
//   { r: "fell", from: "uid", t: 1720800000000, seq: 8 }
//
// Semantics are deliberately one-directional: YOU ONLY EVER SEND
// A RESULT TO THE LOSER. Receiving `r:"fell"` means *your* avatar
// fell on the sender's machine — the duel is decided against you.
// No `winner` field to parse, no perspective math to get wrong.
export const makeResultPayload = (r, from) => ({
  r,
  from: from || "?",
  t: Date.now(),
  seq: ++_seq,
});

/** Validate an incoming RESULT. Only shapes we ship land. */
export const validateResult = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.r !== "fell") return null;       // unknown result kind → drop
  return { r: "fell", from: String(payload.from || "?"), seq: payload.seq | 0 };
};

// ── Send / receive ────────────────────────────────────────────

/**
 * Cast a sabotage at the opponent. NEVER throws. Returns true if
 * it went out, false if there was no transport (solo play, or a
 * network that hasn't connected yet — either way, not an error).
 */
export const send = (powerId, point, from) => {
  if (!transport) return false;
  const pw = getPower(powerId);
  const mv = BOSS_MOVES[powerId] || GUARDIAN_MOVES[powerId];
  if (!pw && !mv) return false;
  if (pw && !pw.sabotage) return false;
  try {
    transport.send(makePayload(powerId, point, from));
    return true;
  } catch (e) {
    console.warn("[SabotageBus] transport threw — dropping:", e?.message);
    return false;
  }
};

/**
 * Called by a transport when a packet lands. Validates, dedupes,
 * and hands off to the local inbox. A transport calls THIS; it
 * never touches an engine directly.
 */
const _seen = new Set();
export const receive = (payload) => {
  // ── Route by SHAPE: a result packet has `r`, a sabotage has `p`.
  //    Two frozen shapes, two gates, one dedupe discipline.
  if (payload && payload.r !== undefined) {
    const rv = validateResult(payload);
    if (!rv) return false;
    const rkey = `r:${rv.from}:${rv.seq}`;   // kind-prefixed — never collides
    if (_seen.has(rkey)) return false;       // with a sabotage's (from,seq)
    _seen.add(rkey);
    if (!resultInbox) return false;
    try { resultInbox(rv); } catch (e) {
      console.warn("[SabotageBus] resultInbox threw:", e?.message);
      return false;
    }
    return true;
  }

  const v = validate(payload);
  if (!v) return false;
  const key = `${v.from}:${v.seq}`;
  if (_seen.has(key)) return false;         // dedupe — RTDB can re-fire
  _seen.add(key);
  if (_seen.size > 500) _seen.clear();      // bound the set; a duel is short
  if (!inbox) return false;
  try { inbox(v); } catch (e) {
    console.warn("[SabotageBus] inbox threw:", e?.message);
    return false;
  }
  return true;
};

/**
 * Tell the LOSER the duel is decided (P2.8 / D19). NEVER throws.
 * Fired by the winner's machine the moment the avatar on their
 * table fells. In the NPC duel both engines are local so this is
 * redundant-but-harmless; over Firebase it is the ONLY way the
 * loser learns.
 */
export const sendResult = (r, from) => {
  if (!transport) return false;
  if (r !== "fell") return false;            // only shapes we ship go out
  try {
    transport.send(makeResultPayload(r, from));
    return true;
  } catch (e) {
    console.warn("[SabotageBus] transport threw on result — dropping:", e?.message);
    return false;
  }
};

export const resetBus = () => {
  unregisterTransport();
  unregisterInbox();
  unregisterResultInbox();
  _seen.clear();
  _seq = 0;
};

// ══════════════════════════════════════════════════════════════
// TRANSPORT 1 — LOCAL (NPC duel, or two-players-one-keyboard)
// Both engines in one tab. The "wire" is a function call, with
// an artificial delay so the NPC doesn't feel like a reflex god
// and so we exercise the same async path PvP uses. Testing the
// async path in single-player is how you avoid discovering the
// race conditions on launch day.
// ══════════════════════════════════════════════════════════════
export const LocalTransport = (targetInbox, delayMs = 140) => ({
  send: (payload) => {
    setTimeout(() => {
      try { targetInbox(payload); } catch {}
    }, delayMs);
  },
  close: () => {},
});

// ══════════════════════════════════════════════════════════════
// TRANSPORT 2 — FIREBASE (real PvP)
// ------------------------------------------------------------
// Writes to  duels/{duelId}/sabotage/{toUid}/{pushKey}
// Listens on duels/{duelId}/sabotage/{myUid}
//
// ⚠ FIREBASE RULES LAW (from Terror, learned the hard way):
//   `$extra: false` means EVERY field written to a node must be
//   whitelisted in the rules, in the SAME deploy. If you add a
//   field to the payload and don't add it to rules.json, the
//   write fails SILENTLY with permission_denied and the sabotage
//   just... never arrives. You will spend an afternoon on it.
//   The payload shape above is FROZEN. To change it: rules first,
//   then code. Backend before frontend. Always.
//
// This factory takes its firebase deps as ARGUMENTS rather than
// importing them, so the bus stays testable and the physics/game
// layers never gain a Firebase dependency.
// ══════════════════════════════════════════════════════════════
export const FirebaseTransport = ({ db, ref, push, set, onValue, off, duelId, myUid, oppUid }) => {
  const outPath  = `duels/${duelId}/sabotage/${oppUid}`;
  const inPath   = `duels/${duelId}/sabotage/${myUid}`;
  // ── THE RESULT LANE (P2.8) — its own node, its own frozen shape.
  //    {r,from,t,seq} would trip `$extra:false` on the sabotage
  //    node, and {p,x,y} would trip it on this one. Two shapes,
  //    two nodes, zero widened rules.
  const outRPath = `duels/${duelId}/result/${oppUid}`;
  const inRPath  = `duels/${duelId}/result/${myUid}`;
  const inRef    = ref(db, inPath);
  const inRRef   = ref(db, inRPath);

  const unsub = onValue(inRef, (snap) => {
    const val = snap.val();
    if (!val) return;
    // Apply every unseen packet. `receive()` dedupes by (from,seq),
    // so re-firing the whole node on every change is safe and we
    // never need to delete-on-read (which would race).
    for (const k of Object.keys(val)) receive(val[k]);
  });
  const unsubR = onValue(inRRef, (snap) => {
    const val = snap.val();
    if (!val) return;
    for (const k of Object.keys(val)) receive(val[k]);   // same dedupe, `r:` keyspace
  });

  return {
    // Route by shape — same rule the receiver applies. One send()
    // so LocalTransport (shape-agnostic) needs no changes at all.
    send: (payload) => {
      const path = payload && payload.r !== undefined ? outRPath : outPath;
      set(push(ref(db, path)), payload);
    },
    close: () => { try { unsub(); } catch {} try { unsubR(); } catch {} },
  };
};

export default {
  registerTransport, unregisterTransport, hasTransport,
  registerInbox, unregisterInbox,
  registerResultInbox, unregisterResultInbox,
  send, sendResult, receive, resetBus,
  validate, validateResult, makePayload, makeResultPayload,
  LocalTransport, FirebaseTransport,
};
